import React from 'react';
import { Button } from '../../../ui/button';
import { RTLInput, RTLTextarea } from '../../../ui/rtl-input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../../../lib/auth/AuthContext';
import { Id } from '../../../../convex/_generated/dataModel';
import { FaqContent } from '../../../../types/pageBuilder';
import { DEFAULT_FAQ_CONTENT } from '../../../../constants/pageBuilderDefaults';
import { useToast } from '../../../ui/use-toast';

interface FaqSectionProps {
  projectId: string;
  selectedLanguage: string;
  faqComponent: any;
  faqContent: FaqContent;
  setFaqContent: React.Dispatch<React.SetStateAction<FaqContent>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refreshComponents: () => void;
}

export const FaqSection: React.FC<FaqSectionProps> = ({
  projectId,
  selectedLanguage,
  faqComponent,
  faqContent,
  setFaqContent,
  isLoading,
  setIsLoading,
  refreshComponents
}) => {
  const { useAuthenticatedMutation } = useAuth();
  const { toast } = useToast();
  const saveComponent = useAuthenticatedMutation('pageComponents:upsert');

  const handleSaveFaqComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID and language are required'
      });
      return;
    }

    console.log('Saving FAQ component with data:', faqContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'faq',
        order: 700, // Seventh component
        content: faqContent,
        isActive: true,
        componentId: faqComponent?._id
      });
      
      console.log('FAQ component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'FAQ section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving FAQ component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save FAQ'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addFaqItem = () => {
    const newId = Date.now().toString();
    setFaqContent(prev => ({
      ...prev,
      faqs: [
        ...prev.faqs,
        {
          id: newId,
          question: '',
          answer: ''
        }
      ]
    }));
  };

  const removeFaqItem = (id: string) => {
    setFaqContent(prev => ({
      ...prev,
      faqs: prev.faqs.filter(faq => faq.id !== id)
    }));
  };

  const updateFaqItem = (id: string, field: 'question' | 'answer', value: string) => {
    setFaqContent(prev => ({
      ...prev,
      faqs: prev.faqs.map(faq => 
        faq.id === id ? { ...faq, [field]: value } : faq
      )
    }));
  };

  const resetToDefault = () => {
    setFaqContent(DEFAULT_FAQ_CONTENT);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>FAQ</CardTitle>
        <CardDescription>
          Add frequently asked questions and answers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">FAQ Items</h3>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={addFaqItem}
            type="button"
          >
            Add FAQ
          </Button>
        </div>
        
        {faqContent.faqs.map((faq, index) => (
          <div key={faq.id} className="border rounded-md p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium">FAQ {index + 1}</h4>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => removeFaqItem(faq.id)}
                className="h-8 text-red-500 hover:text-red-700"
              >
                Remove
              </Button>
            </div>
            
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor={`faq-question-${faq.id}`}>Question</Label>
                <RTLInput
                  id={`faq-question-${faq.id}`}
                  language={selectedLanguage}
                  value={faq.question}
                  onChange={(e) => updateFaqItem(faq.id, 'question', e.target.value)}
                  placeholder="What is the payment plan?"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`faq-answer-${faq.id}`}>Answer</Label>
                <RTLTextarea
                  id={`faq-answer-${faq.id}`}
                  language={selectedLanguage}
                  value={faq.answer}
                  onChange={(e) => updateFaqItem(faq.id, 'answer', e.target.value)}
                  placeholder="The payment plan is as follows..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        ))}
        
        {faqContent.faqs.length === 0 && (
          <div className="text-center p-4 border border-dashed rounded-md">
            <p className="text-sm text-gray-500">No FAQ items added yet. Click the button above to add one.</p>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={resetToDefault}
          disabled={isLoading}
        >
          Reset
        </Button>
        <Button 
          onClick={handleSaveFaqComponent}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : 'Save FAQ'}
        </Button>
      </CardFooter>
    </Card>
  );
}; 